import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { IonTabs } from '@ionic/angular/standalone';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';
import { WavyDividerComponent } from 'src/app/ui-kit/wavy-divider/wavy-divider.component';
import { WorkspaceUIService } from './store';
import { NavigationBarsService } from 'src/app/rxjs-store/navigation-bars';
import { Subscription } from 'rxjs';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';
import { UIState } from 'src/app/rxjs-store/ui';
import { Store } from '@ngxs/store';
import { NavigationPanelComponent } from 'src/app/layout/page/panels/navigation-panel/navigation-panel.component';
import { NavigationPanelContainerComponent } from 'src/app/layout/page/panels/navigation-panel-container/navigation-panel-container.component';
import { NavigationPanelTabsContainerComponent } from 'src/app/layout/page/panels/navigation-panel-tabs-container/navigation-panel-tabs-container.component';
import { HapticFeedbackDirective } from 'src/app/directives';
import { ProfileState } from 'src/app/rxjs-store/account/profile';
import { Profile } from 'src/app/models/schema';

@Component({
  selector: 'app-workspace',
  templateUrl: './workspace.component.html',
  styleUrls: ['./workspace.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonTabs,
    IconComponent,
    WavyDividerComponent,
    IconButtonComponent,
    NavigationPanelComponent,
    NavigationPanelContainerComponent,
    NavigationPanelTabsContainerComponent,
    HapticFeedbackDirective,
  ],
  host: {
    class: 'contents',
  },
})
export class WorkspaceComponent implements OnInit, OnDestroy {
  /**
   * Side menu expanded state subscription
   */
  sideMenuExpandedSubscription: Subscription | null = null;
  sideMenuExpanded = false;

  /**
   * Current user profile subscription
   */
  profileSubscription: Subscription | null = null;

  /**
   * Whether current user can view admin section
   */
  canViewAdminSection = false;


  /**
   * Menu items configuration - My Blume section
   */
  myBlumeMenuItems = [
    {
      label: 'Home',
      icon: 'home-outline',
      route: '/workspace/home',
      color: 'purple',
    },
    {
      label: 'Academy',
      icon: 'school-outline',
      route: '/workspace/academy',
      color: 'yellow',
    },
    {
      label: 'My Account',
      icon: 'person-circle-outline',
      route: '/account',
      color: 'purple',
    },
  ];

  /**
   * Menu items configuration - Admin section
   */
  adminMenuItems = [
    {
      label: 'Users',
      icon: 'key-outline',
      route: '/workspace/users',
      color: 'pink',
    },
    {
      label: 'Students',
      icon: 'people-outline',
      route: '/workspace/students',
      color: 'blue',
    },
    {
      label: 'Observations',
      icon: 'document-text-outline',
      route: '/workspace/observations',
      color: 'green',
    },
    {
      label: 'Goals',
      icon: 'flag-outline',
      route: '/workspace/goals',
      color: 'purple',
    },
    {
      label: 'Development',
      icon: 'code-outline',
      route: '/workspace/development',
      color: 'pink',
    },
    // {
    //   label: 'AI Companion',
    //   icon: 'wand-outline',
    //   route: '/workspace/observations/ai-companion',
    //   color: 'blue',
    // },
    // {
    //   label: 'Activity',
    //   icon: 'pulse',
    //   route: '/workspace/activity',
    //   color: 'green',
    // },
  ];

  constructor(
    private navController: NavController,
    private router: Router,
    private adminUIService: WorkspaceUIService,
    private navBarsService: NavigationBarsService,
    private cdr: ChangeDetectorRef,
    private store: Store
  ) { }


  ngOnInit() {
    // Initialize the admin UI state
    this.adminUIService.initialize();

    this.store
      .select(UIState.isSmallerThanLargeScreen)
      .subscribe((isSmallerThanLargeScreen: boolean) => {
        this.adminUIService.setSideMenuExpanded(!isSmallerThanLargeScreen);
      });

    // Subscribe to side menu expanded state
    this.sideMenuExpandedSubscription =
      this.adminUIService.sideMenuExpanded$.subscribe((expanded: boolean) => {
        this.sideMenuExpanded = expanded;

        // Update global nav visibility based on side menu state
        // When side menu is expanded, hide the bottom nav
        // When side menu is collapsed, show the bottom nav
        this.navBarsService.setBottomNavBarTransparent(expanded);

        this.cdr.markForCheck();
      });

    // Subscribe to current user profile to check admin access
    this.profileSubscription = this.store
      .select(ProfileState.profile)
      .subscribe((profile: Profile | null) => {
        const profileLoaded = this.store.selectSnapshot(ProfileState.loaded);

        console.log('[WorkspaceComponent] Profile loaded:', profileLoaded);
        console.log('[WorkspaceComponent] Current profile:', profile);
        console.log('[WorkspaceComponent] Profile role:', profile?.role);

        // User can view admin section if they have DISTRICT_ADMIN or BUILDING_ADMIN role
        this.canViewAdminSection = this.checkAdminAccess(profile);
        console.log('[WorkspaceComponent] Can view admin section:', this.canViewAdminSection);

        this.cdr.markForCheck();
      });
  }

  toggleSideMenu(): void {
    this.adminUIService.toggleSideMenu();
  }

  ngOnDestroy() {
    if (this.sideMenuExpandedSubscription) {
      this.sideMenuExpandedSubscription.unsubscribe();
    }
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
  }

  /**
   * Check if user has admin access
   * 
   * @param profile - User profile
   * @returns Whether user can view admin section
   */
  private checkAdminAccess(profile: Profile | null): boolean {
    if (!profile) {
      console.log('[WorkspaceComponent] No profile found');
      return false;
    }

    if (!profile.role) {
      console.log('[WorkspaceComponent] Profile has no role');
      return false;
    }

    const hasAccess = profile.role === 'DISTRICT_ADMIN' || profile.role === 'BUILDING_ADMIN';
    console.log('[WorkspaceComponent] Profile has admin access:', hasAccess, '(role:', profile.role + ')');

    return hasAccess;
  }

  /**
   * Navigate to a menu item
   *
   * Uses Angular Router directly instead of NavController to ensure
   * consistent lifecycle hook behavior across all navigation scenarios
   */
  navigateTo(route: string): void {
    const isSmallerThanLargeScreen = this.store.selectSnapshot(
      UIState.isSmallerThanLargeScreen
    );
    this.adminUIService.setSideMenuExpanded(!isSmallerThanLargeScreen);
    this.navController.navigateForward([route]);
  }

  /**
   * Check if current route is active
   */
  isActive(route: string): boolean {
    return this.router.url === route;
  }

}
